// apps/accounts/src/pages/materials/Materials.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Plus, Search, Edit, Trash2, Eye, RefreshCw,
  AlertCircle, CheckCircle, Package, TrendingUp, TrendingDown, X
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const getStatusConfig = (status) => {
  const map = {
    'Available': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
    'Low Stock': { color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
    'Out of Stock': { color: 'bg-red-100 text-red-700', icon: X },
    'Discontinued': { color: 'bg-slate-100 text-slate-700', icon: X },
  };
  return map[status] || map['Available'];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; }
};

const Materials = () => {
  const { user, role } = useAuth();
  const canManage = ['admin', 'site_engineer', 'site_supervisor'].includes(role);
  const canDelete = role === 'admin';

  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [suppliers, setSuppliers] = useState([]); // ✅ changed from vendors
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  const [showForm, setShowForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', category: '', uom: '', min_stock: 0, max_stock: 0,
    reorder_qty: 0, avg_cost: 0, current_stock: 0, supplier: '',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transLoading, setTransLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [cats, uomsRes, suppliersRes, mats] = await Promise.all([
        pb.collection('material_categories').getFullList({ sort: 'name', requestKey: null }),
        pb.collection('material_uoms').getFullList({ sort: 'name', requestKey: null }),
        pb.collection('suppliers').getFullList({ sort: 'companyName', requestKey: null }), // ✅ fetch suppliers
        pb.collection('materials').getFullList({
          sort: 'name',
          expand: 'category,uom,supplier,created_by',
          requestKey: null,
        }),
      ]);
      setCategories(cats);
      setUoms(uomsRes);
      setSuppliers(suppliersRes);
      setMaterials(mats);
    } catch (err) {
      if (err.isAbort) return;
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Refreshed');
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete material "${name}"?`)) return;
    try {
      await pb.collection('materials').delete(id);
      toast.success('Material deleted');
      fetchData();
    } catch (err) {
      toast.error('Delete failed: ' + err.message);
    }
  };

  const openDetail = async (material) => {
    setSelectedMaterial(material);
    setDetailModalOpen(true);
    setTransLoading(true);
    try {
      const txns = await pb.collection('material_transactions').getFullList({
        filter: `material = "${material.id}"`,
        sort: '-date,-created',
        expand: 'user,project',
        requestKey: null,
      });
      setTransactions(txns);
    } catch (err) {
      if (err.isAbort) return;
    } finally {
      setTransLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openCreate = () => {
    setEditingMaterial(null);
    setFormData({ code: '', name: '', description: '', category: '', uom: '', min_stock: 0, max_stock: 0, reorder_qty: 0, avg_cost: 0, current_stock: 0, supplier: '' });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      code: material.code || '',
      name: material.name || '',
      description: material.description || '',
      category: material.category || '',
      uom: material.uom || '',
      min_stock: material.min_stock || 0,
      max_stock: material.max_stock || 0,
      reorder_qty: material.reorder_qty || 0,
      avg_cost: material.avg_cost || 0,
      current_stock: material.current_stock || 0,
      supplier: material.supplier || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = {
        code: formData.code.trim().toUpperCase(),
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category,
        uom: formData.uom,
        min_stock: parseFloat(formData.min_stock) || 0,
        max_stock: parseFloat(formData.max_stock) || 0,
        reorder_qty: parseFloat(formData.reorder_qty) || 0,
        avg_cost: parseFloat(formData.avg_cost) || 0,
        current_stock: parseFloat(formData.current_stock) || 0,
        supplier: formData.supplier || null,
      };
      // Auto-status
      if (data.current_stock <= 0) data.status = 'Out of Stock';
      else if (data.current_stock < data.min_stock) data.status = 'Low Stock';
      else data.status = 'Available';
      if (editingMaterial) {
        await pb.collection('materials').update(editingMaterial.id, data);
        toast.success('Material updated');
      } else {
        await pb.collection('materials').create(data);
        toast.success('Material created');
      }
      setShowForm(false);
      fetchData();
    } catch (err) {
      let msg = err.message || 'Failed to save';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data).map(([k, v]) => `${k}: ${v.message}`).join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleStockUpdate = async (materialId, type, quantity, note = '') => {
    if (!canManage) { toast.error('Permission denied'); return; }
    if (quantity <= 0) { toast.error('Quantity must be positive'); return; }
    try {
      await pb.collection('material_transactions').create({
        material: materialId,
        type,
        quantity,
        date: new Date().toISOString().split('T')[0],
        note: note || (type === 'purchase' ? 'Purchase receipt' : (type === 'issue' ? 'Issued' : 'Stock adjustment')),
        user: user.id,
      });
      const mat = materials.find(m => m.id === materialId);
      const newStock = type === 'purchase' || type === 'adjustment_in'
        ? (mat.current_stock || 0) + quantity
        : Math.max(0, (mat.current_stock || 0) - quantity);
      let status = 'Available';
      if (newStock <= 0) status = 'Out of Stock';
      else if (newStock < mat.min_stock) status = 'Low Stock';
      await pb.collection('materials').update(materialId, { current_stock: newStock, status });
      toast.success(`Stock updated`);
      fetchData();
      if (detailModalOpen) {
        setTransLoading(true);
        const txns = await pb.collection('material_transactions').getFullList({
          filter: `material = "${materialId}"`,
          sort: '-date,-created',
          expand: 'user,project',
          requestKey: null,
        });
        setTransactions(txns);
        setTransLoading(false);
      }
    } catch (err) {
      toast.error('Stock update failed: ' + err.message);
    }
  };

  const filtered = materials.filter(m => {
    const matchSearch = m.name?.toLowerCase().includes(search.toLowerCase()) ||
                        m.code?.toLowerCase().includes(search.toLowerCase());
    const matchCat = filterCategory === 'All' || m.category === filterCategory;
    const matchStatus = filterStatus === 'All' || m.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  });

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400" /></div>;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-amber-500" /> Materials
          </h1>
          <p className="text-sm text-slate-500">Manage construction materials and stock</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canManage && (
            <Button onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-white">
              <Plus className="w-4 h-4 mr-1" /> Add Material
            </Button>
          )}
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <Input placeholder="🔍 Search by name or code..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Status</option>
          <option value="Available">Available</option>
          <option value="Low Stock">Low Stock</option>
          <option value="Out of Stock">Out of Stock</option>
          <option value="Discontinued">Discontinued</option>
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterCategory('All'); setFilterStatus('All'); }}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Code</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Category</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">UOM</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Stock</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Min</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Supplier</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? (
                <tr><td colSpan="9" className="px-4 py-8 text-center text-slate-500">No materials</td></tr>
              ) : (
                filtered.map(m => {
                  const statusConf = getStatusConfig(m.status);
                  const supplierName = m.expand?.supplier?.companyName || m.expand?.supplier?.name || '—';
                  return (
                    <tr key={m.id} className="hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-mono text-xs">{m.code}</td>
                      <td className="px-4 py-3 font-medium">{m.name}</td>
                      <td className="px-4 py-3">{m.expand?.category?.name || '—'}</td>
                      <td className="px-4 py-3">{m.expand?.uom?.symbol || '—'}</td>
                      <td className="px-4 py-3 font-mono">{m.current_stock}</td>
                      <td className="px-4 py-3 font-mono">{m.min_stock}</td>
                      <td className="px-4 py-3">{supplierName}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${statusConf.color} border-0`}>
                          <statusConf.icon className="w-3 h-3 mr-1 inline" />
                          {m.status || '—'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center gap-1">
                          <button onClick={() => openDetail(m)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View">
                            <Eye className="w-4 h-4" />
                          </button>
                          {canManage && (
                            <>
                              <button onClick={() => openEdit(m)} className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" title="Edit">
                                <Edit className="w-4 h-4" />
                              </button>
                              {canDelete && (
                                <button onClick={() => handleDelete(m.id, m.name)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold mb-4">{editingMaterial ? 'Edit Material' : 'New Material'}</h2>
            {formError && <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">{formError}</div>}
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
                  <Input name="code" value={formData.code} onChange={handleFormChange} placeholder="e.g., CEM-001" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <Input name="name" value={formData.name} onChange={handleFormChange} required />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select name="category" value={formData.category} onChange={handleFormChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">UOM *</label>
                  <select name="uom" value={formData.uom} onChange={handleFormChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                    <option value="">Select UOM</option>
                    {uoms.map(u => <option key={u.id} value={u.id}>{u.name} ({u.symbol})</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Min Stock</label>
                  <Input name="min_stock" type="number" value={formData.min_stock} onChange={handleFormChange} step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Max Stock</label>
                  <Input name="max_stock" type="number" value={formData.max_stock} onChange={handleFormChange} step="0.01" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reorder Qty</label>
                  <Input name="reorder_qty" type="number" value={formData.reorder_qty} onChange={handleFormChange} step="0.01" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Avg Cost (₹)</label>
                  <Input name="avg_cost" type="number" value={formData.avg_cost} onChange={handleFormChange} step="0.01" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current Stock</label>
                <Input name="current_stock" type="number" value={formData.current_stock} onChange={handleFormChange} step="0.01" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Supplier</label>
                <select name="supplier" value={formData.supplier} onChange={handleFormChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400">
                  <option value="">Select Supplier</option>
                  {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName || s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Input name="description" value={formData.description} onChange={handleFormChange} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-600" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingMaterial ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedMaterial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold">{selectedMaterial.name}</h2>
                <p className="text-sm text-slate-500">
                  Code: {selectedMaterial.code} | {selectedMaterial.expand?.category?.name} | {selectedMaterial.expand?.uom?.symbol}
                  {selectedMaterial.expand?.supplier && (
                    <> | Supplier: {selectedMaterial.expand.supplier.companyName || selectedMaterial.expand.supplier.name}</>
                  )}
                </p>
              </div>
              <button onClick={() => setDetailModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4 bg-slate-50 p-4 rounded-xl">
              <div><p className="text-xs text-slate-500">Stock</p><p className="text-xl font-bold">{selectedMaterial.current_stock}</p></div>
              <div><p className="text-xs text-slate-500">Min</p><p className="text-xl font-bold">{selectedMaterial.min_stock}</p></div>
              <div><p className="text-xs text-slate-500">Status</p><Badge className={getStatusConfig(selectedMaterial.status).color}>{selectedMaterial.status}</Badge></div>
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2 mb-4 p-3 bg-white border border-slate-200 rounded-lg">
                <span className="text-sm font-medium text-slate-700 mr-2">Update Stock:</span>
                <div className="flex gap-1">
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => {
                    const qty = prompt('Enter quantity to add:');
                    if (qty && !isNaN(qty) && parseFloat(qty) > 0) handleStockUpdate(selectedMaterial.id, 'purchase', parseFloat(qty));
                  }}><TrendingUp className="w-4 h-4 mr-1" /> Add</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => {
                    const qty = prompt('Enter quantity to deduct:');
                    if (qty && !isNaN(qty) && parseFloat(qty) > 0) handleStockUpdate(selectedMaterial.id, 'issue', parseFloat(qty));
                  }}><TrendingDown className="w-4 h-4 mr-1" /> Deduct</Button>
                </div>
              </div>
            )}
            <h3 className="font-semibold mb-2">Transaction History</h3>
            {transLoading ? (
              <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-400" /></div>
            ) : transactions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No transactions</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Type</th>
                      <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Qty</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {transactions.map(t => (
                      <tr key={t.id}>
                        <td className="px-3 py-2">{formatDate(t.date)}</td>
                        <td className="px-3 py-2"><Badge className={t.type === 'purchase' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>{t.type}</Badge></td>
                        <td className="px-3 py-2 text-right font-mono">{t.quantity}</td>
                        <td className="px-3 py-2">{t.note || '—'}</td>
                        <td className="px-3 py-2">{t.expand?.user?.name || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex justify-end mt-4"><Button variant="outline" onClick={() => setDetailModalOpen(false)}>Close</Button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Materials;